import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionState,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import { ConnectionState, Track, type Participant } from 'livekit-client';
import { audioChannelId, type Layer, type LiveKitConnection } from '@liveforge/shared';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { CameraIcon, MicIcon, ScreenShareIcon } from '../components/studio/icons';
import { OnAirToggle } from '../components/studio/on-air-toggle';
import { LayerPanel } from '../components/studio/layer-panel';
import { LayerProperties, type AvailableTrack } from '../components/studio/layer-properties';
import { LayerTransform } from '../components/studio/layer-transform';
import { OutputBar } from '../components/studio/output-bar';
import { Monitor } from '../components/studio/program-preview';
import { SceneStrip } from '../components/studio/scene-strip';
import { SourcesDock } from '../components/studio/source-list';
import { TransitionBar } from '../components/studio/transition-bar';
import { ErrorBoundary } from '../components/ui/error-boundary';
import { LoadingScreen } from '../components/ui/spinner';
import { errorMessage } from '../lib/api';
import { describeDeviceError } from '../lib/media';
import { useLayoutPublisher } from '../hooks/use-layout-publisher';
import { useStudioPersistence } from '../hooks/use-studio-persistence';
import { useRecording } from '../hooks/use-recording';
import { useDismiss } from '../hooks/use-dismiss';
import { useSplitSize } from '../hooks/use-split-size';
import { editingScene, programLayers, topFirst, useStudioStore } from '../stores/studio-store';
import {
  egressToken,
  getHostConnection,
  muteParticipant,
  removeParticipant,
} from '../lib/resources';
import '@livekit/components-styles';
import '../styles/studio.css';

/** Stable identity, so a scene with no layers does not re-render every frame. */
const EMPTY_TREE: Layer[] = [];

function StudioMessage({
  title,
  detail,
  liveId,
}: {
  title: string;
  detail: string;
  liveId: string | undefined;
}) {
  return (
    <div className="studio-message">
      <div className="studio-message__box">
        <h2>{title}</h2>
        <p className="muted">{detail}</p>
        <Link className="button button--primary" to={liveId ? `/lives/${liveId}` : '/'}>
          Back to live
        </Link>
      </div>
    </div>
  );
}

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  [ConnectionState.Disconnected]: 'Disconnected',
  [ConnectionState.Connecting]: 'Connecting',
  [ConnectionState.Connected]: 'Live',
  [ConnectionState.Reconnecting]: 'Reconnecting',
  [ConnectionState.SignalReconnecting]: 'Reconnecting',
};

function ConnectionPill() {
  const state = useConnectionState();
  return <span className="connection-pill">{CONNECTION_LABEL[state] ?? state}</span>;
}

/**
 * Publishes the local camera and microphone once, after the room is connected.
 *
 * LiveKitRoom's `audio`/`video` props acquire the devices as part of connecting,
 * so a host with no webcam gets a NotFoundError that aborts the whole join and
 * retries forever. Not having a camera must not keep you out of your own
 * studio - you still need to see the guests - so the failure is reported and
 * the session continues.
 */
function PublishLocalMedia({ onIssue }: { onIssue: (message: string | null) => void }) {
  const { localParticipant } = useLocalParticipant();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) {
      return;
    }
    attempted.current = true;

    void (async () => {
      const problems: string[] = [];
      try {
        await localParticipant.setMicrophoneEnabled(true);
      } catch (cause) {
        problems.push(describeDeviceError(cause, 'microphone'));
      }
      try {
        await localParticipant.setCameraEnabled(true);
      } catch (cause) {
        problems.push(describeDeviceError(cause, 'camera'));
      }
      onIssue(problems.length > 0 ? problems.join(' ') : null);
    })();
  }, [localParticipant, onIssue]);

  return null;
}

/**
 * Leaving the room. A session-level action, so it stays in the topbar with the
 * room identity and the relay light rather than travelling with the device
 * controls that moved into the side panel.
 */
function LeaveButton({ liveId }: { liveId: string | undefined }) {
  const room = useRoomContext();
  const navigate = useNavigate();

  async function leave() {
    await room.disconnect();
    navigate(liveId ? `/lives/${liveId}` : '/');
  }

  return (
    <button
      type="button"
      className="control-button control-button--leave"
      onClick={() => void leave()}
    >
      Leave
    </button>
  );
}

/**
 * The host's own microphone, camera and screen share, at the foot of the side
 * panel.
 *
 * These used to be a row of labelled pills across the topbar, left over from
 * the Phase 1 video call. They are not directing controls - they say nothing
 * about what reaches the audience - they are the state of this machine's
 * devices, and they duplicated what the source cards a few pixels below
 * already offered. They sit outside the panel's tabs so they are readable
 * whichever tab is open.
 */
function SelfBar({ onIssue }: { onIssue: (message: string | null) => void }) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } =
    useLocalParticipant();
  const [busy, setBusy] = useState(false);

  async function toggle(action: () => Promise<unknown>, kind?: 'camera' | 'microphone') {
    setBusy(true);
    try {
      await action();
      onIssue(null);
    } catch (cause) {
      onIssue(kind ? describeDeviceError(cause, kind) : errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="selfbar">
      <div className="selfbar__who">
        <span className="selfbar__name truncate">
          {localParticipant.name || localParticipant.identity}
        </span>
        <span className="selfbar__role">Director &middot; you</span>
      </div>

      <div className="selfbar__ctl">
        <button
          type="button"
          disabled={busy}
          className={`sb-btn ${isMicrophoneEnabled ? 'sb-btn--on' : 'sb-btn--off'}`}
          title={isMicrophoneEnabled ? 'Mute your microphone' : 'Unmute your microphone'}
          aria-label={isMicrophoneEnabled ? 'Mute your microphone' : 'Unmute your microphone'}
          onClick={() =>
            void toggle(
              () => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled),
              'microphone',
            )
          }
        >
          <MicIcon on={isMicrophoneEnabled} />
        </button>

        <button
          type="button"
          disabled={busy}
          className={`sb-btn ${isCameraEnabled ? 'sb-btn--on' : 'sb-btn--off'}`}
          title={isCameraEnabled ? 'Stop your camera' : 'Start your camera'}
          aria-label={isCameraEnabled ? 'Stop your camera' : 'Start your camera'}
          onClick={() =>
            void toggle(() => localParticipant.setCameraEnabled(!isCameraEnabled), 'camera')
          }
        >
          <CameraIcon on={isCameraEnabled} />
        </button>

        <button
          type="button"
          disabled={busy}
          className={`sb-btn ${isScreenShareEnabled ? 'sb-btn--on' : ''}`}
          title={isScreenShareEnabled ? 'Stop sharing your screen' : 'Share your screen'}
          aria-label={isScreenShareEnabled ? 'Stop sharing your screen' : 'Share your screen'}
          onClick={() =>
            void toggle(() => localParticipant.setScreenShareEnabled(!isScreenShareEnabled))
          }
        >
          <ScreenShareIcon />
        </button>
      </div>
    </div>
  );
}

/**
 * The host's participant list, with the two room-administration controls.
 *
 * Both are server-side LiveKit operations (task F8): `livekit-client` cannot
 * mute or remove anyone from the browser however the token is scoped, so the
 * host token's `roomAdmin` grant is necessary but not sufficient - the call has
 * to go through the API, which re-checks that the caller owns the Live.
 */
function GuestSidebar({ liveId }: { liveId: string | undefined }) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const remotes = participants.filter((p) => p.identity !== localParticipant.identity);

  // Keyed by identity so two slow requests cannot leave the wrong row spinning.
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [pendingKick, setPendingKick] = useState<Participant | null>(null);

  function setRowBusy(identity: string, value: boolean) {
    setBusy((current) => ({ ...current, [identity]: value }));
  }

  async function run(identity: string, action: () => Promise<unknown>) {
    if (!liveId) {
      setError('This studio has no live id, so it cannot administer the room.');
      return;
    }
    setError(null);
    setRowBusy(identity, true);
    try {
      await action();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setRowBusy(identity, false);
    }
  }

  // The server reports the participant's real state back over the room's own
  // signalling, so nothing is set optimistically here: the dot flips when
  // LiveKit says it flipped, not when the request returns.
  function toggleMic(participant: Participant) {
    void run(participant.identity, () =>
      muteParticipant(liveId as string, participant.identity, {
        source: 'microphone',
        muted: participant.isMicrophoneEnabled,
      }),
    );
  }

  function confirmKick() {
    const participant = pendingKick;
    if (!participant) {
      return;
    }
    void run(participant.identity, async () => {
      await removeParticipant(liveId as string, participant.identity);
      setPendingKick(null);
    });
  }

  return (
    <section className="sidebar-section">
      <div className="dock__header">
        <h2>Participants</h2>
        <span className="muted">{remotes.length}</span>
      </div>

      {error ? (
        <p className="alert alert--warning" role="status">
          {error}
        </p>
      ) : null}

      <div className="studio__sidebar-body">
        {remotes.length === 0 ? (
          <p className="muted">No one else has joined. Share an invite link from the live page.</p>
        ) : (
          remotes.map((participant) => (
            <div className="participant-row" key={participant.sid}>
              <div>
                <div className="participant-row__name">
                  {participant.name || participant.identity}
                </div>
                <div className="participant-row__state muted">
                  <span
                    className={`state-dot ${participant.isMicrophoneEnabled ? 'state-dot--on' : 'state-dot--off'}`}
                    aria-hidden="true"
                  />
                  mic
                  <span
                    className={`state-dot ${participant.isCameraEnabled ? 'state-dot--on' : 'state-dot--off'}`}
                    aria-hidden="true"
                  />
                  cam
                </div>
              </div>
              <div className="participant-row__actions">
                <OnAirToggle
                  channelId={audioChannelId(
                    participant.identity,
                    participant.getTrackPublication(Track.Source.Microphone)?.trackName || null,
                  )}
                />
                <Button
                  variant="ghost"
                  small
                  loading={busy[participant.identity] ?? false}
                  onClick={() => toggleMic(participant)}
                  title="Switches off the guest's own microphone. They see it, and LiveKit will not let it be switched back on remotely."
                >
                  {participant.isMicrophoneEnabled ? 'Mute mic' : 'Unmute mic'}
                </Button>
                <Button
                  variant="ghost"
                  small
                  disabled={busy[participant.identity] ?? false}
                  onClick={() => setPendingKick(participant)}
                >
                  Kick
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={pendingKick !== null}
        title="Remove this participant?"
        message={
          pendingKick
            ? `${pendingKick.name || pendingKick.identity} will be disconnected immediately. ` +
              'Their invite link keeps working, so they can rejoin.'
            : ''
        }
        confirmLabel="Remove"
        busy={pendingKick ? (busy[pendingKick.identity] ?? false) : false}
        onConfirm={confirmKick}
        onCancel={() => setPendingKick(null)}
      />
    </section>
  );
}

/**
 * The host's local capture sources (Phase 2). Lives inside `LiveKitRoom`
 * because the tracks it owns are published through the room's local
 * participant, and it must unmount - releasing every device - with it.
 */
/**
 * The compositing half of the studio: the layer tree, the properties of the
 * selected layer, and the programme preview that renders both.
 *
 * It lives inside `LiveKitRoom` because the preview draws the room's tracks,
 * and it publishes the tree to the relay so a Layout Page - the host's own
 * preview today, Egress in Phase 5 - renders exactly the same thing.
 */
/**
 * Opens the real Layout Page in a window of its own.
 *
 * Worth having for its own sake - a director can put the programme on a second
 * screen - but it earns its place by being the only way to see the composition
 * drawn by something that is **not** this browser. The detached window
 * connects to the room with its own subscribe-only token and reads the layer
 * tree from the relay, exactly as the headless Chrome inside Egress will. If
 * it matches the programme monitor, the output pipeline has a foundation; the
 * studio's own monitor could never prove that, because it renders from the
 * same store it is being compared against.
 */
function useDetachedProgram(liveId: string | undefined) {
  const [busy, setBusy] = useState(false);

  const open = useCallback(() => {
    if (!liveId || busy) return;

    /*
     * A window of its own, not another tab: this is a monitor, and a director
     * wants it on the second screen rather than behind the studio.
     *
     * The feature string is what decides that - Chrome gives you a tab when
     * there is none. Note the exact opposite choice for the YouTube player in
     * `source-list.tsx`, which deliberately passes nothing: a popup is offered
     * to the capture picker under "Window", and only a "Tab" carries audio.
     * Nothing captures this one, so here a window is simply better.
     *
     * Sized to the largest 16:9 that fits comfortably on this screen. The page
     * scales its 1920x1080 stage to whatever it is given, so the number only
     * decides how big the picture is, never what it contains.
     *
     * A first approximation and nothing more: these are outer dimensions, so
     * the frame and the origin strip a popup carries come out of the viewport
     * and the window is never quite 16:9. It does not matter - the page
     * letterboxes on whichever axis is short, and the window is the person's
     * to size.
     */
    const width = Math.min(1280, Math.round(window.screen.availWidth * 0.8));
    const height = Math.round(width * (9 / 16));

    // Opened now, empty, and pointed somewhere once the token arrives. A
    // window.open() after an await has lost the click that justified it and
    // browsers block it as a popup.
    const target = window.open(
      '',
      `liveforge-program-${liveId}`,
      `popup=yes,width=${width},height=${height}`,
    );
    if (!target) {
      window.alert('Your browser blocked the window. Allow pop-ups for this site and try again.');
      return;
    }
    target.document.title = 'LiveForge - Program';

    setBusy(true);
    egressToken(liveId)
      .then((connection) => {
        const url = new URL('/layout/', window.location.origin);
        url.searchParams.set('liveId', liveId);
        url.searchParams.set('token', connection.token);
        url.searchParams.set('livekitUrl', connection.url);
        // Scales the 1920x1080 stage into whatever window this is. Egress
        // never passes it: its viewport is exactly that size already.
        url.searchParams.set('fit', '1');
        target.location.replace(url.toString());
      })
      .catch((cause: unknown) => {
        target.close();
        window.alert(`Could not open the program window: ${errorMessage(cause)}`);
      })
      .finally(() => setBusy(false));
  }, [liveId, busy]);

  return { open, busy };
}

/**
 * Quick or studio.
 *
 * The difference is not cosmetic and the labels say so: in quick mode a
 * scene click goes straight to air and every edit is already being
 * broadcast, while studio mode stages everything behind a transition.
 */
function Compositing({ liveId }: { liveId: string | undefined }) {
  const mode = useStudioStore((state) => state.mode);
  // The scene being edited: the previewed one in studio mode, the on-air one
  // in quick mode, where there is nowhere else to work.
  const layers = useStudioStore((state) => editingScene(state)?.layers ?? EMPTY_TREE);
  // What is actually going out, which in studio mode is a frozen snapshot and
  // is the only tree that ever reaches the relay.
  const program = useStudioStore(programLayers);
  const audio = useStudioStore((state) => state.audio);
  const mediaLabels = useStudioStore((state) => state.mediaLabels);
  const selectedId = useStudioStore((state) => state.selectedId);
  const revision = useStudioStore((state) => state.revision);
  const select = useStudioStore((state) => state.select);
  const addLayer = useStudioStore((state) => state.addLayer);
  const removeLayer = useStudioStore((state) => state.removeLayer);
  const updateLayer = useStudioStore((state) => state.updateLayer);
  const reorder = useStudioStore((state) => state.reorder);

  // A quarter of the window to begin with, then whatever the director drags it
  // to. Persisted, because a size you have to re-drag every session is barely
  // better than a fixed one.
  const { size: monitorHeight, handleProps } = useSplitSize(
    'liveforge.studio.monitorHeight',
    Math.round(window.innerHeight * 0.26),
    // The take column between the monitors has a floor: two labelled fields,
    // the TAKE button and the line under it. Without a minimum the drag
    // squeezes the row under that and the column spills over the docks.
    { axis: 'y', min: 260 },
  );

  // The two dock columns the director sizes; the layer list takes what is
  // left, because it is the one that grows without bound.
  const scenesSplit = useSplitSize('liveforge.studio.scenesWidth.v2', 420, {
    axis: 'x',
    // Both badges, the three row actions, and enough left over that a few
    // characters of the name are always readable - a row truncated to nothing
    // tells the director less than no row at all.
    min: 340,
    minRemaining: 420,
  });
  // Properties floats rather than living in the dock row, so it needs no
  // column of its own.
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const propertiesRef = useDismiss<HTMLDivElement>(propertiesOpen, () => setPropertiesOpen(false));

  // The programme, never the scene being edited: only what is on air is
  // published, which is what makes the studio-mode snapshot mean anything.
  // The reason matters as much as the fact: a red dot on its own was the whole
  // diagnosis, and on 2026-08-19 that cost about an hour with the answer one
  // click away in DevTools.
  const { connected: synced, error: syncError } = useLayoutPublisher(
    liveId,
    program,
    audio,
    revision,
  );

  // The tracks a layer can be pointed at. `Unknown` is where the host's extra
  // multicam sources are published, so leaving it out would make them
  // uncompositable - the one thing Phase 2 exists to allow.
  const trackRefs = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
      { source: Track.Source.Unknown, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const available: AvailableTrack[] = trackRefs
    .filter((reference) => reference.publication !== undefined)
    .map((reference) => {
      const publication = reference.publication;
      const who = reference.participant.name || reference.participant.identity;
      const trackName = publication?.trackName ?? '';
      const what =
        reference.source === Track.Source.ScreenShare
          ? 'screen'
          : publication?.trackName || 'camera';
      return {
        sid: publication?.trackSid ?? '',
        participantIdentity: reference.participant.identity,
        trackName,
        // A clip is named after what it is playing, not after whoever happens
        // to be publishing it: "YOUTUBE - <title>" rather than "Fabrizio -
        // media-1", which told the director nothing.
        label: mediaLabels[trackName] ?? `${who} - ${what}`,
        isScreenShare: reference.source === Track.Source.ScreenShare,
      };
    })
    .filter((track) => track.sid !== '');

  const selected = layers.find((layer) => layer.id === selectedId);
  const detach = useDetachedProgram(liveId);

  return (
    <>
      <div
        className={`studio__monitors ${mode === 'quick' ? 'studio__monitors--single' : ''}`}
        style={{ height: monitorHeight }}
      >
        {/*
          Two monitors in studio mode, one in quick mode. The preview is the
          scene being edited; the programme is what the audience has, which in
          studio mode is a snapshot and does not follow these edits.
        */}
        {mode === 'studio' ? (
          <Monitor
            label="Preview"
            layers={layers}
            // The director sees the empty slots; the audience never does.
            showPlaceholders
            // Dragging is offered on the preview only. In quick mode the
            // programme *is* the scene being edited, and free-hand dragging on
            // a live broadcast is a different decision from this one.
            overlay={
              <LayerTransform
                layer={selected}
                onChange={(patch) => selected && updateLayer(selected.id, patch)}
              />
            }
          />
        ) : null}
        {/*
          Between the two monitors, not under them: preview on the left, air on
          the right, and the control that moves one into the other in the
          middle. It renders nothing in quick mode, so the row is left with the
          single programme monitor.
        */}
        <TransitionBar />

        <Monitor
          label="Program"
          layers={program}
          onAir
          synced={synced}
          syncError={syncError}
          onDetach={liveId ? detach.open : undefined}
          detaching={detach.busy}
        />
      </div>

      {syncError ? (
        <p className="alert alert--warning" role="status">
          The program is not reaching the layout channel: {syncError} Guests and any recording are
          showing the last composition that got through.
        </p>
      ) : null}

      {/* Drag to trade monitor size against room for the docks. */}
      <div {...handleProps} aria-label="Resize the monitors" />

      {/*
        Two columns, not three. Properties left the dock row for a panel that
        floats: it is consulted while adjusting one layer, not read alongside
        the others, and it was costing a permanent third of the width.
      */}
      <div
        className="studio__docks"
        style={{ gridTemplateColumns: `${scenesSplit.size}px auto minmax(0, 1fr)` }}
      >
        <SceneStrip />
        <div {...scenesSplit.handleProps} aria-label="Resize the scene list" />
        <LayerPanel
          layers={topFirst(layers)}
          selectedId={selectedId}
          onSelect={select}
          onAdd={addLayer}
          onRemove={removeLayer}
          onUpdate={updateLayer}
          onReorder={reorder}
          onEdit={() => setPropertiesOpen(true)}
        />
      </div>

      {propertiesOpen && selected ? (
        <div
          className="properties-popup"
          role="dialog"
          aria-label={`Properties: ${selected.name}`}
          ref={propertiesRef}
        >
          <div className="properties-popup__header">
            <h2>{selected.name}</h2>
            <button
              type="button"
              className="icon-button"
              title="Close"
              onClick={() => setPropertiesOpen(false)}
            >
              ✕
            </button>
          </div>
          <div className="properties-popup__body">
            <LayerProperties layer={selected} tracks={available} onUpdate={updateLayer} />
          </div>
        </div>
      ) : null}
    </>
  );
}

export function StudioPage() {
  const { liveId } = useParams<{ liveId: string }>();
  const navigate = useNavigate();
  const [connection, setConnection] = useState<LiveKitConnection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deviceIssue, setDeviceIssue] = useState<string | null>(null);
  // Open by default: with no live thumbnails yet, this panel is the only way
  // to see who is actually in the room.
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sideTab, setSideTab] = useState<'participants' | 'sources'>('sources');

  // The side panel is sized like everything else here. It grows towards the
  // start of the axis, hence invert.
  const sidebarSplit = useSplitSize('liveforge.studio.sidebarWidth', 340, {
    axis: 'x',
    invert: true,
    // One card wide. Past 500 the list becomes a grid of them.
    min: 250,
    minRemaining: 640,
  });
  // Stable identity so PublishLocalMedia's effect does not re-run every render.
  const handleIssue = useCallback((message: string | null) => setDeviceIssue(message), []);

  // In the page and not in the monitors, because the output belongs to the
  // session: the topbar carries it, and it keeps polling whichever dock or
  // scene the director is working in.
  const recording = useRecording(liveId);

  // Above the early returns, so the scenes are being fetched while the room
  // token is: neither waits on the other.
  const { loadError, saveError } = useStudioPersistence(liveId);

  useEffect(() => {
    if (!liveId) {
      setError('No live was requested.');
      return;
    }
    const controller = new AbortController();
    getHostConnection(liveId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setConnection(data);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      });
    return () => controller.abort();
  }, [liveId]);

  if (error) {
    return <StudioMessage title="Could not open the studio" detail={error} liveId={liveId} />;
  }

  if (!connection) {
    return <LoadingScreen label="Requesting a room token..." />;
  }

  return (
    <LiveKitRoom
      className="studio"
      data-lk-theme="default"
      token={connection.token}
      serverUrl={connection.url}
      connect
      // Deliberately false: devices are published after connecting, by
      // PublishLocalMedia. Acquiring them here makes a missing camera abort
      // the join entirely.
      audio={false}
      video={false}
      onError={(cause) => setError(errorMessage(cause))}
      onDisconnected={() => navigate(liveId ? `/lives/${liveId}` : '/')}
    >
      <div className="studio__topbar">
        {/* The studio is still the product: it carries the mark like every
            other page, then says which room this is. */}
        <Link to="/dashboard" className="studio__brand">
          <span className="studio__brand-mark" aria-hidden="true" />
          LiveForge
        </Link>
        <span className="studio__sep" aria-hidden="true" />
        <span className="studio__title">{connection.roomName}</span>
        <div className="studio__topbar-end">
          {/* What is leaving this studio, before the way out of it. */}
          <OutputBar recording={recording} />
          <span className="studio__sep" aria-hidden="true" />
          <ConnectionPill />
          <LeaveButton liveId={liveId} />
        </div>
      </div>

      <PublishLocalMedia onIssue={handleIssue} />

      {deviceIssue ? (
        <p className="alert alert--warning" role="status">
          {deviceIssue} You are still connected, and can see and hear everyone else.
        </p>
      ) : null}

      {/*
        Both of these mean the same thing to a director - what is on screen is
        not what is stored - but they fail at opposite ends, so they say which.
      */}
      {loadError ? (
        <p className="alert alert--error" role="alert">
          Could not load this live&rsquo;s scenes: {loadError} You are starting from an empty scene,
          and nothing you build here will be saved.
        </p>
      ) : null}

      {saveError ? (
        <p className="alert alert--warning" role="alert">
          Your scenes are not being saved: {saveError} They are safe until you close this tab.
        </p>
      ) : null}

      {/*
        The video-call grid is gone on purpose: this is a gallery, not a
        conversation. Participants are sources now - they reach the audience by
        being placed in a scene, or by an AUTO layer that places them all.

        The monitors are height-capped rather than width-capped: at 16:9 across
        a whole screen they leave the controls a sliver, and a director spends
        far more time in the docks than staring at a large preview.
      */}
      <div className="studio__workspace">
        <div className="studio__main">
          {/*
            Around the composition rather than the whole page: if a layer or a
            source throws, the topbar stays and the director can still leave the
            room cleanly instead of losing the tab to a black screen.
          */}
          <ErrorBoundary label="studio">
            <Compositing liveId={liveId} />
          </ErrorBoundary>
        </div>

        {/* No handle on a collapsed rail: there is nothing there to size. */}
        {sidebarOpen ? (
          <div {...sidebarSplit.handleProps} aria-label="Resize the side panel" />
        ) : null}

        <aside
          className={`studio__sidebar ${sidebarOpen ? '' : 'studio__sidebar--collapsed'}`}
          style={sidebarOpen ? { width: sidebarSplit.size } : undefined}
        >
          {/* Attached to the panel it opens, rather than adrift in the topbar. */}
          <button
            type="button"
            className="studio__sidebar-toggle"
            onClick={() => setSidebarOpen((open) => !open)}
            title={sidebarOpen ? 'Hide this panel' : 'Show participants and sources'}
            aria-label={sidebarOpen ? 'Hide this panel' : 'Show participants and sources'}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? '›' : '‹'}
          </button>

          {/*
            Both tabs stay mounted and the inactive one is hidden, never
            unmounted: SourcesDock owns the host's published multicam tracks,
            and tearing it down to look at the participant list would stop
            them being published.
          */}
          <div className="studio__sidebar-inner" hidden={!sidebarOpen}>
            <div className="panel-tabs" role="tablist">
              {(['participants', 'sources'] as const).map((name) => (
                <button
                  key={name}
                  type="button"
                  role="tab"
                  aria-selected={sideTab === name}
                  className={`panel-tabs__tab ${sideTab === name ? 'panel-tabs__tab--on' : ''}`}
                  onClick={() => setSideTab(name)}
                >
                  {name === 'participants' ? 'Participants' : 'Sources'}
                </button>
              ))}
            </div>

            <div className="studio__sidebar-pane" hidden={sideTab !== 'participants'}>
              <GuestSidebar liveId={liveId} />
            </div>
            <div className="studio__sidebar-pane" hidden={sideTab !== 'sources'}>
              <ErrorBoundary label="sources panel">
                <SourcesDock />
              </ErrorBoundary>
            </div>

            <SelfBar onIssue={handleIssue} />
          </div>
        </aside>
      </div>

      {/* Plays every remote audio track; without it guests are silent. */}
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}
